import React, { useState, useEffect } from "react"
import { useSearchParams } from "react-router-dom"
import { useAuth } from "../../../contexts/AuthContext"
import { 
  ChatBubbleLeftRightIcon, 
  CheckCircleIcon,
  ArrowPathIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
  FunnelIcon,
  DocumentTextIcon,
  PhoneIcon,
  CheckIcon,
  ExclamationTriangleIcon,
  ChatBubbleBottomCenterTextIcon,
  DocumentDuplicateIcon,
  ClipboardDocumentCheckIcon,
  ArrowDownTrayIcon
} from "@heroicons/react/24/outline"

const CampaignConversationalReport = () => {
  const { 
    fetchCampaigns, 
    fetchCampaignConversationalReport, 
    fetchRecipientConversationHistory,
    exportCampaignConversationalReport 
  } = useAuth()
  
  const [searchParams, setSearchParams] = useSearchParams()
  const campaignIdFromUrl = searchParams.get("campaignId") || ""

  // State management
  const [campaignsList, setCampaignsList] = useState([])
  const [selectedCampaignId, setSelectedCampaignId] = useState(campaignIdFromUrl)
  const [campaignData, setCampaignData] = useState(null)
  const [stats, setStats] = useState({ total: 0, sent: 0, delivered: 0, read: 0, failed: 0, replied: 0 })
  const [recipients, setRecipients] = useState([])
  
  const [loading, setLoading] = useState(false)
  const [campaignsLoading, setCampaignsLoading] = useState(false)
  const [exporting, setExporting] = useState(false)

  // Selection & Copy State
  const [selectedNumbers, setSelectedNumbers] = useState([])
  const [copiedId, setCopiedId] = useState(null)
  const [bulkCopied, setBulkCopied] = useState(false)

  // Filters
  const [statusFilter, setStatusFilter] = useState("replied")
  const [numberSearch, setNumberSearch] = useState("")
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage, setItemsPerPage] = useState(10)
  const [totalPages, setTotalPages] = useState(1)
  const [totalItems, setTotalItems] = useState(0)

  // Chat modal state
  const [selectedRecipient, setSelectedRecipient] = useState(null)
  const [showChatModal, setShowChatModal] = useState(false)
  const [chatMessages, setChatMessages] = useState([])
  const [chatLoading, setChatLoading] = useState(false)

  // Load campaigns list for selector
  useEffect(() => {
    const loadCampaignsList = async () => {
      setCampaignsLoading(true)
      try {
        const res = await fetchCampaigns({}, 1, 100)
        const list = res.data || res.campaigns || []
        setCampaignsList(list)
        
        // If no campaign selected in state or URL, default to first campaign if available
        if (!selectedCampaignId && list.length > 0) {
          const firstId = list[0]._id || list[0].id
          setSelectedCampaignId(firstId)
        }
      } catch (err) {
        console.error("Error loading campaigns list:", err)
      } finally {
        setCampaignsLoading(false)
      }
    }
    loadCampaignsList()
  }, [])

  // Sync selected campaign with URL parameter if it changes
  useEffect(() => {
    if (campaignIdFromUrl && campaignIdFromUrl !== selectedCampaignId) {
      setSelectedCampaignId(campaignIdFromUrl)
    }
  }, [campaignIdFromUrl])

  // Load Conversational Report for selected campaign
  const loadConversationalReport = async () => {
    if (!selectedCampaignId) return
    setLoading(true)
    setSelectedNumbers([])
    try {
      const filters = {
        status: statusFilter,
        numberSearch: numberSearch
      }
      const res = await fetchCampaignConversationalReport(selectedCampaignId, filters, currentPage, itemsPerPage)
      if (res && res.success) {
        setCampaignData(res.campaign)
        setStats(res.stats)
        setRecipients(res.recipients)
        setTotalPages(res.pagination?.pages || 1)
        setTotalItems(res.pagination?.total || 0)
      }
    } catch (err) {
      console.error("Error loading conversational report:", err)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (selectedCampaignId) {
      loadConversationalReport()
    }
  }, [selectedCampaignId, statusFilter, currentPage, itemsPerPage])

  const handleSearchSubmit = (e) => {
    e.preventDefault()
    setCurrentPage(1)
    loadConversationalReport()
  }

  const handleCampaignChange = (e) => {
    const newId = e.target.value
    setSelectedCampaignId(newId)
    setCurrentPage(1)
    setSearchParams(newId ? { campaignId: newId } : {})
  }

  const handleOpenChat = async (recipient) => {
    setSelectedRecipient(recipient)
    setShowChatModal(true)
    setChatLoading(true)
    try {
      const res = await fetchRecipientConversationHistory(selectedCampaignId, recipient.number)
      if (res && res.success) {
        setChatMessages(res.messages || [])
      } else {
        setChatMessages([])
      }
    } catch (err) {
      console.error("Error fetching recipient chat history:", err)
      setChatMessages([])
    } finally {
      setChatLoading(false)
    }
  }

  // Copy number handlers
  const handleCopyNumber = (num, id) => {
    if (!num) return
    navigator.clipboard.writeText(num)
    setCopiedId(id)
    setTimeout(() => setCopiedId(null), 2000)
  }

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedNumbers(recipients.map(r => r.number).filter(Boolean))
    } else {
      setSelectedNumbers([])
    }
  }

  const handleSelectRow = (number) => {
    setSelectedNumbers(prev => 
      prev.includes(number) ? prev.filter(n => n !== number) : [...prev, number]
    )
  }

  const handleBulkCopy = () => {
    if (selectedNumbers.length === 0) return
    const text = selectedNumbers.join(", ")
    navigator.clipboard.writeText(text)
    setBulkCopied(true)
    setTimeout(() => setBulkCopied(false), 2000)
  }

  // Export Excel function
  const handleExportExcel = async () => {
    if (!selectedCampaignId) return
    setExporting(true)
    try {
      if (typeof exportCampaignConversationalReport === "function") {
        await exportCampaignConversationalReport(selectedCampaignId, {
          status: statusFilter,
          numberSearch: numberSearch
        })
      } else {
        // Fallback browser CSV export matching exact table form structure
        const formatDate = (d) => {
          if (!d) return "-"
          const dateObj = new Date(d)
          if (isNaN(dateObj.getTime())) return "-"
          return dateObj.toLocaleString("en-GB", {
            day: "2-digit", month: "2-digit", year: "numeric",
            hour: "2-digit", minute: "2-digit", hour12: true
          })
        }

        const rows = [
          [`"camp date"`, `"${formatDate(campaignData?.createdAt)}"`, `"camp name"`, `"${campaignData?.campaignName || 'Campaign'}"`, `"camp id"`, `"${selectedCampaignId}"`],
          [`"sent"`, `"${stats.sent}"`, `"delivered"`, `"${stats.delivered}"`, `"read"`, `"${stats.read}"`, `"failed"`, `"${stats.failed}"`, `"replied"`, `"${stats.replied}"`, `"total"`, `"${stats.total}"`],
          [],
          [`"mobile no"`, `"sent date"`, `"delivered date"`, `"sent message"`, `"sent on"`, `"received msg"`, `"received on"`]
        ]

        recipients.forEach(r => {
          const mobNo = r.number
          const sentDateFormatted = formatDate(r.sentAt || r.createdAt)
          const deliveredDateFormatted = formatDate(r.deliveredAt)
          const templateMsg = r.templateName || campaignData?.templateName || "Template Message"

          const thread = [
            { isOut: true, text: templateMsg, time: sentDateFormatted }
          ]
          if (r.isReplied && r.lastReply) {
            thread.push({
              isOut: false,
              text: r.lastReply.message || "",
              time: r.lastReply.createdAt ? formatDate(r.lastReply.createdAt) : sentDateFormatted
            })
          }

          thread.forEach((msg, idx) => {
            const isFirst = idx === 0
            rows.push([
              `"${isFirst ? mobNo : ''}"`,
              `"${isFirst ? sentDateFormatted : ''}"`,
              `"${isFirst ? deliveredDateFormatted : ''}"`,
              `"${msg.isOut ? msg.text.replace(/"/g, '""') : ''}"`,
              `"${msg.isOut ? msg.time : ''}"`,
              `"${!msg.isOut ? msg.text.replace(/"/g, '""') : ''}"`,
              `"${!msg.isOut ? msg.time : ''}"`
            ])
          })
          rows.push([])
        })

        const csvContent = "data:text/csv;charset=utf-8,\uFEFF" + rows.map(e => e.join(",")).join("\n")
        const encodedUri = encodeURI(csvContent)
        const link = document.createElement("a")
        link.setAttribute("href", encodedUri)
        link.setAttribute("download", `Conversational_Report_${selectedCampaignId}.csv`)
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
      }
    } catch (err) {
      console.error("Error exporting excel:", err)
    } finally {
      setExporting(false)
    }
  }

  const getStatusBadge = (status) => {
    switch (status?.toLowerCase()) {
      case "read":
        return "bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-400 border border-blue-200 dark:border-blue-800"
      case "delivered":
        return "bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-400 border border-green-200 dark:border-green-800"
      case "sent":
        return "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-800"
      case "failed":
        return "bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-400 border border-red-200 dark:border-red-800"
      default:
        return "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-400 border border-yellow-200 dark:border-yellow-800"
    }
  }

  const getPaginationPages = () => {
    const pages = []
    const maxVisible = 5
    let start = Math.max(1, currentPage - 2)
    let end = Math.min(totalPages, start + maxVisible - 1)

    if (end - start + 1 < maxVisible) {
      start = Math.max(1, end - maxVisible + 1)
    }

    if (start > 1) {
      pages.push(1)
      if (start > 2) pages.push("...")
    }

    for (let i = start; i <= end; i++) {
      pages.push(i)
    }

    if (end < totalPages) {
      if (end < totalPages - 1) pages.push("...")
      pages.push(totalPages)
    }

    return pages
  }

  const repliedPercent = stats.total > 0 ? Math.round((stats.replied / stats.total) * 100) : 0
  const isAllSelected = recipients.length > 0 && recipients.every(r => selectedNumbers.includes(r.number))

  return (
    <div className="p-6 w-full space-y-6">
      {/* Header & Campaign Selector */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white flex items-center gap-3">
            <ChatBubbleLeftRightIcon className="w-8 h-8 text-emerald-600 dark:text-emerald-400" />
            Campaign Conversational Report
          </h1>
          <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
            Track full recipient delivery statuses, responses, and interactive message threads.
          </p>
        </div>

        {/* Campaign Selector Dropdown */}
        <div className="w-full md:w-80 bg-white dark:bg-gray-800 p-3.5 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
          <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1.5">
            Select Campaign
          </label>
          <select
            value={selectedCampaignId}
            onChange={handleCampaignChange}
            disabled={campaignsLoading}
            className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-emerald-500 focus:border-emerald-500 dark:bg-gray-700 dark:text-white font-medium"
          >
            <option value="">-- Choose a Campaign --</option>
            {campaignsList.map((c) => (
              <option key={c._id || c.id} value={c._id || c.id}>
                {c.campaignName || c.name || "Unnamed Campaign"} ({c.templateName || "Template"})
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Active Campaign Overview Banner Card */}
      {campaignData && (
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-4 border-b border-gray-100 dark:border-gray-700">
            <div>
              <span className="inline-block px-2.5 py-0.5 bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 rounded-full text-xs font-semibold mb-2 border border-emerald-200 dark:border-emerald-800">
                Active Campaign Report
              </span>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white tracking-tight">{campaignData.campaignName}</h2>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1.5 flex items-center gap-1.5">
                <DocumentTextIcon className="w-4 h-4 text-gray-400" /> Template: <strong className="text-gray-700 dark:text-gray-200 font-semibold">{campaignData.templateName}</strong>
              </p>
            </div>
            <div className="text-left sm:text-right">
              <span className="text-xs text-gray-500 dark:text-gray-400 block font-medium">Created On</span>
              <span className="text-sm font-semibold text-gray-800 dark:text-gray-200">{new Date(campaignData.createdAt).toLocaleString()}</span>
            </div>
          </div>

          {/* Metric Cards Grid */}
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-3.5 mt-6">
            <div className="bg-gray-50 dark:bg-gray-700/40 rounded-xl p-3.5 text-center border border-gray-200 dark:border-gray-600">
              <span className="text-xs text-gray-500 dark:text-gray-400 font-semibold block uppercase tracking-wider">Total Numbers</span>
              <span className="text-2xl font-bold mt-1 block text-gray-900 dark:text-white">{stats.total}</span>
            </div>
            <div className="bg-gray-50 dark:bg-gray-700/40 rounded-xl p-3.5 text-center border border-gray-200 dark:border-gray-600">
              <span className="text-xs text-gray-500 dark:text-gray-400 font-semibold block uppercase tracking-wider">Sent</span>
              <span className="text-2xl font-bold mt-1 block text-emerald-600 dark:text-emerald-400">{stats.sent}</span>
            </div>
            <div className="bg-gray-50 dark:bg-gray-700/40 rounded-xl p-3.5 text-center border border-gray-200 dark:border-gray-600">
              <span className="text-xs text-gray-500 dark:text-gray-400 font-semibold block uppercase tracking-wider">Delivered</span>
              <span className="text-2xl font-bold mt-1 block text-green-600 dark:text-green-400">{stats.delivered}</span>
            </div>
            <div className="bg-gray-50 dark:bg-gray-700/40 rounded-xl p-3.5 text-center border border-gray-200 dark:border-gray-600">
              <span className="text-xs text-gray-500 dark:text-gray-400 font-semibold block uppercase tracking-wider">Read</span>
              <span className="text-2xl font-bold mt-1 block text-blue-600 dark:text-blue-400">{stats.read}</span>
            </div>
            <div className="bg-gray-50 dark:bg-gray-700/40 rounded-xl p-3.5 text-center border border-gray-200 dark:border-gray-600">
              <span className="text-xs text-gray-500 dark:text-gray-400 font-semibold block uppercase tracking-wider">Failed</span>
              <span className="text-2xl font-bold mt-1 block text-red-600 dark:text-red-400">{stats.failed}</span>
            </div>
            <div className="bg-emerald-50 dark:bg-emerald-950/40 rounded-xl p-3.5 text-center border border-emerald-200 dark:border-emerald-800">
              <span className="text-xs text-emerald-700 dark:text-emerald-400 font-semibold block uppercase tracking-wider">Replied ({repliedPercent}%)</span>
              <span className="text-2xl font-bold mt-1 block text-emerald-700 dark:text-emerald-300">{stats.replied}</span>
            </div>
          </div>
        </div>
      )}

      {/* Filters Card */}
      <div className="bg-white dark:bg-gray-800 p-5 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
        <form onSubmit={handleSearchSubmit} className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 items-end">
          <div>
            <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-2 flex items-center gap-1.5">
              <MagnifyingGlassIcon className="w-4 h-4 text-emerald-600" /> Search Number
            </label>
            <input
              type="text"
              value={numberSearch}
              onChange={(e) => setNumberSearch(e.target.value)}
              placeholder="Enter phone number..."
              className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-emerald-500 focus:border-emerald-500 dark:bg-gray-700 dark:text-white"
            />
          </div>

          

          <div>
            <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-2">
              Rows Per Page
            </label>
            <select
              value={itemsPerPage}
              onChange={(e) => {
                const val = e.target.value === "all" ? "all" : Number(e.target.value)
                setItemsPerPage(val)
                setCurrentPage(1)
              }}
              className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-emerald-500 focus:border-emerald-500 dark:bg-gray-700 dark:text-white font-medium"
            >
              <option value={10}>10 per page</option>
              <option value={20}>20 per page</option>
              <option value={50}>50 per page</option>
              <option value={100}>100 per page</option>
              <option value="all">See all</option>
            </select>
          </div>

          <div className="flex gap-2 flex-wrap sm:flex-nowrap">
            <button
              type="submit"
              className="bg-emerald-600 hover:bg-emerald-700 text-white font-medium px-4 py-2 text-sm rounded-md shadow-sm transition-colors flex items-center justify-center gap-1.5"
            >
              <MagnifyingGlassIcon className="w-4 h-4" /> Filter
            </button>
            <button
              type="button"
              onClick={() => {
                setNumberSearch("")
                setStatusFilter("replied")
                setCurrentPage(1)
                loadConversationalReport()
              }}
              className="bg-gray-100 hover:bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600 font-medium px-4 py-2 text-sm rounded-md transition-colors"
            >
              Reset
            </button>
            <button
              type="button"
              onClick={handleExportExcel}
              disabled={exporting || !selectedCampaignId}
              className="bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white font-medium px-4 py-2 text-sm rounded-md shadow-sm transition-colors flex items-center justify-center gap-1.5 whitespace-nowrap ml-auto"
              title="Export all chat logs & recipient metrics to Excel"
            >
              <ArrowDownTrayIcon className={`w-4 h-4 ${exporting ? 'animate-bounce' : ''}`} />
              {exporting ? "Exporting..." : "Export Excel"}
            </button>
          </div>
        </form>
      </div>

      {/* Recipients Conversational Report Table Card */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
        <div className="p-5 border-b border-gray-200 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-800/50 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
              <ChatBubbleBottomCenterTextIcon className="w-5 h-5 text-emerald-600" />
              Recipients Conversational Status
            </h2>
            {selectedNumbers.length > 0 && (
              <button
                onClick={handleBulkCopy}
                className="inline-flex items-center gap-1.5 px-3 py-1 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold rounded-lg transition-all shadow-xs"
              >
                {bulkCopied ? (
                  <>
                    <ClipboardDocumentCheckIcon className="w-4 h-4" /> Copied ({selectedNumbers.length})!
                  </>
                ) : (
                  <>
                    <DocumentDuplicateIcon className="w-4 h-4" /> Copy Selected ({selectedNumbers.length})
                  </>
                )}
              </button>
            )}
          </div>
          <span className="text-xs font-medium text-gray-500 dark:text-gray-400">
            Showing {recipients.length} of {totalItems} recipients
          </span>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead className="bg-gray-50 dark:bg-gray-800/90 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider border-b border-gray-200 dark:border-gray-700">
              <tr>
                <th className="px-4 py-3.5 w-10 text-center">
                  <input
                    type="checkbox"
                    checked={isAllSelected}
                    onChange={handleSelectAll}
                    className="w-4 h-4 text-emerald-600 rounded border-gray-300 focus:ring-emerald-500 cursor-pointer"
                    title="Select All"
                  />
                </th>
                <th className="px-4 py-3.5">Recipient Number</th>
                <th className="px-4 py-3.5">Template</th>
                <th className="px-4 py-3.5">Status</th>
                <th className="px-4 py-3.5">Sent At</th>
                <th className="px-4 py-3.5">Replied Status</th>
                <th className="px-4 py-3.5">Last Reply Message</th>
                <th className="px-4 py-3.5 text-center">Action / Chat</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700 text-sm">
              {loading ? (
                <tr>
                  <td colSpan="8" className="p-12 text-center">
                    <div className="flex flex-col items-center justify-center">
                      <ArrowPathIcon className="w-8 h-8 text-emerald-600 animate-spin mb-2" />
                      <span className="text-sm text-gray-500 dark:text-gray-400">Loading recipient conversation report...</span>
                    </div>
                  </td>
                </tr>
              ) : recipients.length === 0 ? (
                <tr>
                  <td colSpan="8" className="p-12 text-center text-gray-500 dark:text-gray-400">
                    No recipient records found matching your selection.
                  </td>
                </tr>
              ) : (
                recipients.map((rec) => {
                  const isSelected = selectedNumbers.includes(rec.number)
                  return (
                    <tr
                      key={rec._id}
                      className={`hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors ${
                        isSelected ? "bg-emerald-50/50 dark:bg-emerald-950/20" : ""
                      }`}
                    >
                      <td className="px-4 py-4 w-10 text-center">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => handleSelectRow(rec.number)}
                          className="w-4 h-4 text-emerald-600 rounded border-gray-300 focus:ring-emerald-500 cursor-pointer"
                        />
                      </td>
                      <td className="px-4 py-4 font-semibold text-gray-900 dark:text-white whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          <PhoneIcon className="w-4 h-4 text-gray-400 shrink-0" />
                          <span className="select-all cursor-text font-mono tracking-tight">{rec.number}</span>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation()
                              handleCopyNumber(rec.number, rec._id)
                            }}
                            className="p-1 text-gray-400 hover:text-emerald-600 dark:hover:text-emerald-400 transition-colors rounded hover:bg-gray-100 dark:hover:bg-gray-700"
                            title="Copy Phone Number"
                          >
                            {copiedId === rec._id ? (
                              <span className="text-[10px] font-bold text-emerald-600 dark:text-emerald-400 flex items-center gap-0.5">
                                <CheckIcon className="w-3.5 h-3.5" /> Copied!
                              </span>
                            ) : (
                              <DocumentDuplicateIcon className="w-4 h-4" />
                            )}
                          </button>
                        </div>
                      </td>
                      <td className="px-4 py-4 text-gray-600 dark:text-gray-300 max-w-xs truncate font-medium">
                        {rec.templateName}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-semibold ${getStatusBadge(rec.status)}`}>
                          {rec.status.toUpperCase()}
                        </span>
                      </td>
                      <td className="px-4 py-4 text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">
                        {rec.sentAt ? new Date(rec.sentAt).toLocaleString() : "-"}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        {rec.isReplied ? (
                          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-800">
                            <CheckCircleIcon className="w-4 h-4 text-emerald-600 dark:text-emerald-400" /> Yes (Replied)
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400 border border-gray-200 dark:border-gray-600">
                            No (Not Replied)
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-4 max-w-xs truncate">
                        {rec.isReplied && rec.lastReply ? (
                          <div className="inline-flex items-center gap-1.5 px-3 py-1 bg-gray-100 dark:bg-gray-700 rounded-lg text-xs font-medium text-gray-800 dark:text-gray-200 border border-gray-200 dark:border-gray-600 max-w-full truncate">
                            <ChatBubbleLeftRightIcon className="w-4 h-4 text-emerald-600 shrink-0" />
                            <span className="truncate">{rec.lastReply.message}</span>
                          </div>
                        ) : (
                          <span className="text-xs text-gray-400 italic">No reply received</span>
                        )}
                      </td>
                      <td className="px-4 py-4 text-center whitespace-nowrap">
                        <button
                          onClick={() => handleOpenChat(rec)}
                          className={`inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                            rec.isReplied 
                              ? "bg-emerald-600 hover:bg-emerald-700 text-white shadow-sm" 
                              : "bg-gray-100 hover:bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600"
                          }`}
                          title={rec.isReplied ? "View Chat Thread" : "View Outgoing Message"}
                        >
                          <ChatBubbleLeftRightIcon className="w-4 h-4" />
                          {rec.isReplied ? "View Chat" : "View Details"}
                        </button>
                      </td>
                    </tr>
                  )
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Improved Pagination Footer */}
        {totalItems > 0 && (
          <div className="px-4 sm:px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex flex-col sm:flex-row items-center justify-between space-y-3 sm:space-y-0">
            <div className="text-sm text-gray-700 dark:text-gray-300">
              Showing {itemsPerPage === "all" ? 1 : (currentPage - 1) * itemsPerPage + 1} to {itemsPerPage === "all" ? totalItems : Math.min(currentPage * itemsPerPage, totalItems)} of {totalItems} recipients
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
                className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-gray-800 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
              >
                Previous
              </button>

              {getPaginationPages().map((p, idx) =>
                p === "..." ? (
                  <span key={`dot-${idx}`} className="px-2 text-sm text-gray-500">
                    ...
                  </span>
                ) : (
                  <button
                    key={`page-${p}`}
                    onClick={() => setCurrentPage(p)}
                    disabled={p === currentPage}
                    className={`px-3 py-2 text-sm font-medium rounded-md border ${
                      p === currentPage
                        ? "bg-emerald-600 text-white border-emerald-600"
                        : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-600 dark:hover:bg-gray-700"
                    }`}
                  >
                    {p}
                  </button>
                )
              )}

              <button
                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                disabled={currentPage === totalPages}
                className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-gray-800 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Chat Thread Modal */}
      {showChatModal && selectedRecipient && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-xs flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-2xl w-full overflow-hidden border border-gray-100 dark:border-gray-700">
            {/* Modal Header */}
            <div className="p-5 bg-gradient-to-r from-emerald-800 to-teal-800 text-white flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center text-white font-bold text-lg">
                  📱
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="text-lg font-bold select-all">{selectedRecipient.number}</h3>
                    <button
                      type="button"
                      onClick={() => handleCopyNumber(selectedRecipient.number, "modal_" + selectedRecipient.number)}
                      className="p-1 hover:bg-white/20 rounded transition-colors text-white"
                      title="Copy Number"
                    >
                      {copiedId === "modal_" + selectedRecipient.number ? (
                        <span className="text-[10px] font-bold bg-white text-emerald-800 px-1.5 py-0.5 rounded">Copied!</span>
                      ) : (
                        <DocumentDuplicateIcon className="w-4 h-4" />
                      )}
                    </button>
                  </div>
                  <p className="text-xs text-emerald-200">
                    Template: {selectedRecipient.templateName} &bull; Status: {selectedRecipient.status.toUpperCase()}
                  </p>
                </div>
              </div>
              <button
                onClick={() => setShowChatModal(false)}
                className="text-emerald-200 hover:text-white p-1 rounded-lg hover:bg-white/10"
              >
                <XMarkIcon className="w-6 h-6" />
              </button>
            </div>

            {/* Modal Body / Chat Messages */}
            <div className="p-6 max-h-[60vh] overflow-y-auto bg-gray-50/50 dark:bg-gray-900/50 space-y-4">
              {chatLoading ? (
                <div className="p-8 text-center">
                  <ArrowPathIcon className="w-8 h-8 text-emerald-600 animate-spin mx-auto mb-2" />
                  <p className="text-sm text-gray-500">Loading conversation history...</p>
                </div>
              ) : !selectedRecipient.isReplied && chatMessages.length <= 1 ? (
                <div className="space-y-4">
                  {/* Notice card for No Reply */}
                  <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl p-4 flex items-start gap-3">
                    <ExclamationTriangleIcon className="w-6 h-6 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
                    <div>
                      <h4 className="text-sm font-semibold text-amber-800 dark:text-amber-300">No Reply Received</h4>
                      <p className="text-xs text-amber-700 dark:text-amber-400 mt-1">
                        Recipient <strong className="font-mono select-all">{selectedRecipient.number}</strong> has not sent any reply to this campaign message yet.
                      </p>
                    </div>
                  </div>

                  {/* Outbound Template Message */}
                  <div className="flex flex-col items-end">
                    <div className="bg-emerald-700 text-white p-4 rounded-2xl rounded-tr-xs max-w-md shadow-sm">
                      <span className="text-[10px] font-semibold tracking-wider uppercase text-emerald-200 block mb-1">
                        Outbound Campaign Message
                      </span>
                      <p className="text-sm font-medium">
                        [Template: {selectedRecipient.templateName}]
                      </p>
                      <div className="flex items-center justify-end gap-1 mt-2 text-[10px] text-emerald-200">
                        <span>{selectedRecipient.sentAt ? new Date(selectedRecipient.sentAt).toLocaleTimeString() : "Sent"}</span>
                        <CheckIcon className="w-3.5 h-3.5" />
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  {chatMessages.map((msg, index) => (
                    <div
                      key={msg._id || index}
                      className={`flex flex-col ${msg.direction === "out" ? "items-end" : "items-start"}`}
                    >
                      <div
                        className={`p-4 rounded-2xl max-w-md shadow-sm text-sm ${
                          msg.direction === "out"
                            ? "bg-emerald-700 text-white rounded-tr-xs"
                            : "bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 rounded-tl-xs border border-gray-200 dark:border-gray-700"
                        }`}
                      >
                        <span
                          className={`text-[10px] font-semibold tracking-wider uppercase block mb-1 ${
                            msg.direction === "out" ? "text-emerald-200" : "text-emerald-600 dark:text-emerald-400"
                          }`}
                        >
                          {msg.direction === "out" ? "Outbound Message (Business)" : `Incoming Reply (${selectedRecipient.number})`}
                        </span>
                        
                        <p className="whitespace-pre-wrap select-text">{msg.message}</p>

                        <div
                          className={`flex items-center justify-end gap-1 mt-2 text-[10px] ${
                            msg.direction === "out" ? "text-emerald-200" : "text-gray-400"
                          }`}
                        >
                          <span>{new Date(msg.createdAt).toLocaleTimeString()}</span>
                          {msg.direction === "out" && <CheckIcon className="w-3.5 h-3.5" />}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="p-4 bg-gray-50 dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 flex justify-end">
              <button
                onClick={() => setShowChatModal(false)}
                className="px-5 py-2 text-sm font-medium bg-gray-200 hover:bg-gray-300 text-gray-800 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600 rounded-xl transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default CampaignConversationalReport
